/**
 * Native encrypt as resolution-gating (Milestone 13 / roadmap §20.2), Phase 1.
 *
 * A commitment's content (the withholdable `target + quantity` closure, Milestone 9) is normally either present (the
 * commitment is *complete*) or omitted (it is *dark* — a missing observation for everyone else). [SealedContent]
 * adds a third supply form that is **holdable yet unreadable**: the content closure's [LinkPreimage]s are
 * [SecretBox]-sealed under a content-key, and only a holder of a matching [Capability] can recover them.
 *
 * The key property is that confidentiality is *not* a bolt-on flag — it is resolution. To an observer without the
 * capability a sealed node resolves to [com.oasisfeng.invaluable.data.Observation.Missing], the **same dark state**
 * as an omitted preimage (no distinct "encrypted" observation). Meanwhile:
 *
 * - **Attribution is unchanged**: the spine still names the *plaintext* content projection by digest, so the head
 *   projection, signature binding, and valuation of the complete path are byte-identical to Milestone 9.
 * - **Verify ≠ read**: anyone can verify *possession* of the bytes against [ciphertextDigest] without the plaintext;
 *   only a capability holder can [open] them.
 * - **Tamper-evident twice**: a corrupted ciphertext fails the Poly1305 tag in [open]; even a validly-sealed wrong
 *   closure fails the existing `projectLink == projection` self-check inside `resolveLink`. Either way it degrades to
 *   missing, never a synthesized placeholder.
 *
 * Phase 1 kept the capability as the raw symmetric content-key; Phase 2 added wrap-to-identity ([CapabilityWrap]).
 * Phase 3 makes [SealedContent] a [ReplicatedEvent] so the ciphertext travels the event family / store / sync and can
 * be redundantly custodied (§20.1) — the **wraps stay off the wire** (they are regenerable directed envelopes).
 */

package com.oasisfeng.invaluable.event

import com.oasisfeng.invaluable.data.LinkProjection
import com.oasisfeng.invaluable.data.ProjectedLinkRecord
import com.oasisfeng.invaluable.data.ProjectedLinkRecordResolver
import com.oasisfeng.invaluable.identity.Codec
import com.oasisfeng.invaluable.identity.SecretBox
import kotlinx.io.bytestring.ByteString
import kotlin.jvm.JvmInline

/**
 * A read capability for sealed content: the symmetric content-key that opens a [SealedContent].
 *
 * Phase 1 holds the raw [SecretBox] key directly; a disclosure distributes it as a [CapabilityWrap] — a directed
 * envelope boxed to one recipient's identity-derived encryption key (§20.3), never a shared roster.
 */
@JvmInline
value class Capability(val key: ByteString) {
    init { require(key.size == SecretBox.KEY_SIZE) { "Capability key must be ${SecretBox.KEY_SIZE} bytes." } }
}

/**
 * The sealed (encrypted) wire form of one commitment-content closure — the third replicated object (Phase 3).
 *
 * Unlike a [LinkPreimage] it is not self-verifying against [contentProjection] without a capability (an observer
 * without the key cannot tell whether the ciphertext opens to that projection), so it is accepted on possession alone:
 * it is an opaque carrier whose integrity is re-checked at *read* time inside [CapabilityResolver]/`resolveLink`. This
 * is exactly the §20.1 split — anyone can hold and replicate the ciphertext (custody), only a key-holder can read it.
 *
 * @property contentProjection Projection of the *plaintext* content node — what the spine's digest binds (attribution
 *   stays plaintext-bound). Resolution of this projection is what a held capability unlocks.
 * @property nonce 24-byte [SecretBox] nonce.
 * @property ciphertext `tag || ciphertext` of the content closure's preimage lines under the content-key.
 */
data class SealedContent(
    val contentProjection: LinkProjection,
    val nonce: ByteString,
    val ciphertext: ByteString,
) : ReplicatedEvent {

    /** SHA-256 of [ciphertext]: the possession oracle. Anyone can confirm a custodian holds the bytes, without reading. */
    val ciphertextDigest: ByteString by lazy { ByteString(*Codec.sha256(ciphertext.toByteArray())) }

    /**
     * Recover the content closure's records with [capability], or null if it does not open this seal or the plaintext
     * is malformed. The recovered records are still self-verified against their projections by `resolveLink`.
     */
    fun open(capability: Capability, codec: WireCodec): List<ProjectedLinkRecord>? {
        val plaintext = SecretBox.open(capability.key.toByteArray(), nonce.toByteArray(), ciphertext.toByteArray())
            ?: return null
        val records = mutableListOf<ProjectedLinkRecord>()
        for (line in plaintext.decodeToString().lineSequence()) {
            if (line.isEmpty()) continue
            records += (codec.decode(line) as? LinkPreimage)?.record ?: return null
        }
        return records
    }

    companion object {
        private const val LINE_SEPARATOR = "\n"

        /**
         * Seal a content closure under [capability]. [preimages] is the committed content's closure (e.g.
         * [PublishedCommitment.content]); [contentProjection] is that closure's plaintext root projection.
         */
        fun seal(
            contentProjection: LinkProjection,
            preimages: List<LinkPreimage>,
            capability: Capability,
            nonce: ByteString,
            codec: WireCodec,
        ): SealedContent {
            val plaintext = preimages.joinToString(LINE_SEPARATOR, transform = codec::encode).encodeToByteArray()
            val ciphertext = SecretBox.seal(capability.key.toByteArray(), nonce.toByteArray(), plaintext)
            return SealedContent(contentProjection, nonce, ByteString(*ciphertext))
        }
    }
}

/**
 * A capability-aware [ProjectedLinkRecordResolver] decorator — the resolution-gating step (§20.2 layer-3, the
 * `combine` extension).
 *
 * It delegates to [base] first; for a projection [base] cannot resolve, it serves from the closures of any held
 * [SealedContent] whose *bound* capability opens it. So a capability holder resolves sealed content exactly as if its
 * preimages were present, while a non-holder gets [base]'s plain missing observation — the sealed content stays dark,
 * with no special-cased "encrypted" state. The decorator is additive: non-sealed projections are untouched.
 *
 * [capabilities] is keyed by the [content projection][SealedContent.contentProjection] each key unlocks, so a seal is
 * only ever tried against the *one* key bound to its claimed projection — never the whole key set. This keeps the model
 * clean (a key is a capability *for one content projection*, not a master key) and, crucially, bounds cost: sealed
 * ciphertext we hold no matching key for is skipped without a decrypt attempt, so custodying unreadable seals (an
 * attacker piling junk carriers at us) costs nothing here. Resolution is bounded by the seals we can actually read, not
 * by how many we custody — closing the eager `sealed × keys` amplification.
 */
class CapabilityResolver(
    private val base: ProjectedLinkRecordResolver,
    sealed: Iterable<SealedContent>,
    capabilities: Map<LinkProjection, Capability>,
    codec: WireCodec,
) : ProjectedLinkRecordResolver {

    // Open each seal with at most the one key bound to its claimed content projection, overlaying the recovered closures.
    private val overlay: Map<LinkProjection, ProjectedLinkRecord> = buildMap {
        for (content in sealed) {
            val records = capabilities[content.contentProjection]?.let { content.open(it, codec) } ?: continue
            for (record in records) if (record.projection !in this) this[record.projection] = record
        }
    }

    override fun recordFor(projection: LinkProjection): ProjectedLinkRecord? =
        base.recordFor(projection) ?: overlay[projection]
}
